/** 워크스페이스별 수업 알람 설정 (LS) */

export type ClassLessonAlarmSoundKind = 'basic' | 'activity';
export type ClassLessonAlarmOffset = '1M' | '3M' | '5M';

export interface ClassLessonAlarmChannelSettings {
  /** 해당 채널 알람 사용 */
  enabled: boolean;
  soundKind: ClassLessonAlarmSoundKind;
  offset: ClassLessonAlarmOffset;
}

export interface ClassLessonAlarmSettings {
  v: 1;
  /** 전체 수업 알람 on/off — 끄면 블록·울림 모두 중지 */
  masterEnabled: boolean;
  /** 대시보드 프레젠테이션 탭이 열려 있으면 알람 모달을 프레젠테이션에만 표시(메인 탭 팝업 숨김) */
  presentationOnly: boolean;
  /** 전담색이 적용되지 않은 수업 */
  regular: ClassLessonAlarmChannelSettings;
  /** 전담색이 적용된 수업 */
  dedicated: ClassLessonAlarmChannelSettings;
}

export const CLASS_LESSON_ALARM_SOUND_OPTIONS: Array<{
  value: ClassLessonAlarmSoundKind;
  label: string;
}> = [
  { value: 'basic', label: '기본 알람' },
  { value: 'activity', label: '활동 알람' },
];

export const CLASS_LESSON_ALARM_OFFSET_OPTIONS: Array<{
  value: ClassLessonAlarmOffset;
  label: string;
}> = [
  { value: '1M', label: '1분 전' },
  { value: '3M', label: '3분 전' },
  { value: '5M', label: '5분 전' },
];

export const CLASS_LESSON_ALARM_SETTINGS_CHANGED_EVENT = 'cp:class-lesson-alarm-settings-changed';

/** 설정 모달에서 스케줄 무시하고 즉시 알람 팝업·소리 실행 */
export const CLASS_LESSON_ALARM_TEST_FIRE_EVENT = 'cp:class-lesson-alarm-test-fire';

/** 팝업닫기 후 네비바 종 두근두근 표시 */
export const CLASS_LESSON_ALARM_ATTENTION_EVENT = 'cp:class-lesson-alarm-attention';

/** 네비바 종 클릭으로 최소화 팝업 다시 열기 */
export const CLASS_LESSON_ALARM_REOPEN_EVENT = 'cp:class-lesson-alarm-reopen';

export type ClassLessonAlarmTestFireDetail = {
  workspaceId: string;
  offset: ClassLessonAlarmOffset;
  soundKind: ClassLessonAlarmSoundKind;
};

export type ClassLessonAlarmAttentionDetail = {
  workspaceId: string;
  active: boolean;
};

export type ClassLessonAlarmReopenDetail = {
  workspaceId: string;
};

const CACHE_VERSION = 'v1';

function storageKey(workspaceId: string): string {
  return `cp.classLessonAlarmSettings.${CACHE_VERSION}.${workspaceId}`;
}

function defaultChannel(): ClassLessonAlarmChannelSettings {
  return {
    enabled: true,
    soundKind: 'basic',
    offset: '3M',
  };
}

export function createDefaultClassLessonAlarmSettings(): ClassLessonAlarmSettings {
  return {
    v: 1,
    masterEnabled: true,
    presentationOnly: false,
    regular: defaultChannel(),
    dedicated: defaultChannel(),
  };
}

function normalizeChannel(raw: unknown): ClassLessonAlarmChannelSettings {
  const base = defaultChannel();
  if (!raw || typeof raw !== 'object') return base;
  const o = raw as Record<string, unknown>;
  const soundKind =
    o.soundKind === 'activity' || o.soundKind === 'basic' ? o.soundKind : base.soundKind;
  const offset =
    o.offset === '1M' || o.offset === '3M' || o.offset === '5M' ? o.offset : base.offset;
  return {
    enabled: o.enabled === false ? false : true,
    soundKind,
    offset,
  };
}

export function normalizeClassLessonAlarmSettings(raw: unknown): ClassLessonAlarmSettings {
  if (!raw || typeof raw !== 'object') return createDefaultClassLessonAlarmSettings();
  const o = raw as Record<string, unknown>;
  return {
    v: 1,
    masterEnabled: o.masterEnabled === false ? false : true,
    presentationOnly: o.presentationOnly === true,
    regular: normalizeChannel(o.regular),
    dedicated: normalizeChannel(o.dedicated),
  };
}

export function loadClassLessonAlarmSettings(workspaceId: string): ClassLessonAlarmSettings {
  const sid = workspaceId.trim();
  if (!sid || typeof window === 'undefined') return createDefaultClassLessonAlarmSettings();
  try {
    const raw = window.localStorage.getItem(storageKey(sid));
    if (!raw) return createDefaultClassLessonAlarmSettings();
    return normalizeClassLessonAlarmSettings(JSON.parse(raw));
  } catch {
    return createDefaultClassLessonAlarmSettings();
  }
}

export function saveClassLessonAlarmSettings(
  workspaceId: string,
  settings: ClassLessonAlarmSettings
): void {
  const sid = workspaceId.trim();
  if (!sid || typeof window === 'undefined') return;
  const normalized = normalizeClassLessonAlarmSettings(settings);
  window.localStorage.setItem(storageKey(sid), JSON.stringify(normalized));
  window.dispatchEvent(
    new CustomEvent(CLASS_LESSON_ALARM_SETTINGS_CHANGED_EVENT, {
      detail: { workspaceId: sid },
    })
  );
}

export function isClassLessonAlarmMasterEnabled(workspaceId: string): boolean {
  return loadClassLessonAlarmSettings(workspaceId).masterEnabled;
}

export function classLessonAlarmOffsetMinutes(offset: ClassLessonAlarmOffset): number {
  if (offset === '1M') return 1;
  if (offset === '5M') return 5;
  return 3;
}

export function classLessonAlarmOffsetLabel(offset: ClassLessonAlarmOffset): string {
  return CLASS_LESSON_ALARM_OFFSET_OPTIONS.find((o) => o.value === offset)?.label ?? offset;
}

export function classLessonAlarmSoundLabel(kind: ClassLessonAlarmSoundKind): string {
  return CLASS_LESSON_ALARM_SOUND_OPTIONS.find((o) => o.value === kind)?.label ?? kind;
}
